import { createSystem, defaultConfig } from "@chakra-ui/react"
import type { SystemStyleObject } from "@chakra-ui/react"
import { buttonRecipe } from "./theme/button.recipe"

const globalStyles = {
  ":root": {
    fontSize: "16px",
    scrollBehavior: "smooth",
  },
  body: {
    fontSize: "0.875rem",
    margin: 0,
    padding: 0,
    minHeight: "100vh",
    overflowX: "hidden",
  },
  ".main-link": {
    color: "ui.main",
    fontWeight: "bold",
    transition: "all 0.2s",
    "&:hover": {
      textDecoration: "underline",
      opacity: 0.8,
    },
  },
  "@media (max-width: 768px)": {
    ":root": {
      fontSize: "14px",
    },
  },
  "*:focus-visible": {
    outline: "2px solid",
    outlineColor: "ui.main",
    outlineOffset: "2px",
  },
} as Record<string, SystemStyleObject>

export const system = createSystem(defaultConfig, {
  globalCss: globalStyles,
  theme: {
    tokens: {
      colors: {
        ui: {
          // Swim-themed color palette (cyan/blue water colors)
          main: { value: "#0891B2" }, // cyan-600 - primary brand
          mainHover: { value: "#0E7490" }, // cyan-700 - hover state
          mainActive: { value: "#155E75" }, // cyan-800 - active state
          secondary: { value: "#0EA5E9" }, // sky-500 - accent
          background: { value: "#FFFFFF" },
          backgroundAlt: { value: "#F0F9FF" }, // sky-50 - light blue tint
          border: { value: "#E0F2FE" }, // sky-100 - subtle blue border
          text: { value: "#0F172A" }, // slate-900 - dark text
          textSecondary: { value: "#64748B" }, // slate-500 - secondary text
          error: { value: "#EF4444" }, // red-500
          warning: { value: "#F59E0B" }, // amber-500
          success: { value: "#10B981" }, // emerald-500
          info: { value: "#0EA5E9" }, // sky-500
        },
      },
      // Responsive breakpoints
      breakpoints: {
        xs: { value: "320px" },
        sm: { value: "640px" },
        md: { value: "768px" },
        lg: { value: "1024px" },
        xl: { value: "1280px" },
        "2xl": { value: "1536px" },
      },
      // Spacing scale
      spacing: {
        xs: { value: "0.25rem" },
        sm: { value: "0.5rem" },
        md: { value: "1rem" },
        lg: { value: "1.5rem" },
        xl: { value: "2rem" },
        "2xl": { value: "3rem" },
        "3xl": { value: "4rem" },
      },
      // Font sizes
      fontSizes: {
        xs: { value: "0.75rem" },
        sm: { value: "0.875rem" },
        md: { value: "1rem" },
        lg: { value: "1.125rem" },
        xl: { value: "1.25rem" },
        "2xl": { value: "1.5rem" },
        "3xl": { value: "1.875rem" },
        "4xl": { value: "2.25rem" },
      },
      // Shadows
      shadows: {
        xs: { value: "0 1px 2px 0 rgba(0, 0, 0, 0.05)" },
        sm: { value: "0 1px 3px 0 rgba(0, 0, 0, 0.1)" },
        md: { value: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" },
        lg: { value: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" },
        xl: { value: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" },
      },
      // Border radius
      radii: {
        none: { value: "0" },
        sm: { value: "0.125rem" },
        md: { value: "0.375rem" },
        lg: { value: "0.5rem" },
        xl: { value: "0.75rem" },
        "2xl": { value: "1rem" },
        full: { value: "9999px" },
      },
      // Transition durations and easings
      durations: {
        fast: { value: "150ms" },
        normal: { value: "250ms" },
        slow: { value: "350ms" },
      },
      easings: {
        standard: { value: "ease-in-out" },
      },
    },
    recipes: {
      button: buttonRecipe,
    },
    // Semantic tokens for automatic dark mode support
    semanticTokens: {
      colors: {
        background: {
          value: { _light: "{colors.ui.background}", _dark: "#0F172A" },
        },
        foreground: {
          value: { _light: "{colors.ui.text}", _dark: "#F1F5F9" },
        },
        border: {
          value: { _light: "{colors.ui.border}", _dark: "#334155" },
        },
        primary: {
          value: { _light: "{colors.ui.main}", _dark: "#22D3EE" },
        },
      },
    },
  },
})
